import { AloftPrice, applyRoundingAndThresholds } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectPoolAdj, selectPoolAdjs } from './selectPoolAdj.ts';
import { selectSpaAdj, selectSpaAdjs } from './selectSpaAdj.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculatePoolSpaAdj = (
  poolAdj: AloftPrice = AloftPrice.zero,
  spaAdj: AloftPrice = AloftPrice.zero,
) => [poolAdj, spaAdj].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);

const selectPoolSpaAdj = createPriceSelector([selectPoolAdj, selectSpaAdj], calculatePoolSpaAdj);

const selectPoolSpaAdjs = createSelector(
  [selectPoolAdjs, selectSpaAdjs, selectCompSequence],
  (poolAdjs, spaAdjs, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculatePoolSpaAdj(poolAdjs[propID], spaAdjs[propID]);

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedPoolSpaAdj = createPriceSelector(
  [selectPoolSpaAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'pool', adjSettings),
);

export const selectAppliedPoolSpaAdjDisplay = createSelector([selectAppliedPoolSpaAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedPoolSpaAdjsDisplay = createSelector(
  [selectPoolSpaAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentEntities[propID]?.poolSpa;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adjs[propID], 'pool', adjSettings),
          ),
          color: getColorFromAdjustment(adjs[propID]),
        };
      }

      return acc;
    }, {}),
);
